<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { type Money } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { RowView } from '@/features/entries'
import type { LoanPaymentDraft, LoanRowMatch } from '../composables/use-loan-payment-extension.ts'
import { estimatePaymentSplit } from '../internal/payment-split.ts'

const props = defineProps<{ match: LoanRowMatch; draft: LoanPaymentDraft; view: RowView }>()

const { t } = useI18n()

const split = computed(() => {
  const posted = props.draft.posted
  if (posted !== undefined) {
    return {
      interest: posted.interestPortion,
      principal: posted.principalPortion,
      overpayment: posted.overpaymentPortion,
    }
  }
  const amount = props.view.total
  if (
    props.match.mode !== 'payment' ||
    amount === undefined ||
    amount.currency !== props.match.loan.currency
  ) {
    return undefined
  }
  return estimatePaymentSplit(amount, props.match.loan)
})

function parts(value: Money) {
  return value.units === 0n ? undefined : value
}

const figures = computed(() =>
  split.value === undefined
    ? []
    : [
        { key: 'interest', label: t('loans.extension.interestFirst'), parts: parts(split.value.interest) },
        { key: 'principal', label: t('loans.principalPortion'), parts: parts(split.value.principal) },
        {
          key: 'overpayment',
          label: t('loans.extension.overpayment'),
          parts: parts(split.value.overpayment),
        },
      ],
)
</script>

<template>
  <div v-if="split !== undefined" class="flex flex-col gap-[var(--space-hair)]">
    <dl class="flex flex-wrap items-baseline gap-x-[var(--space-block)] gap-y-[var(--space-hair)]">
      <div v-for="figure in figures" :key="figure.key" class="flex items-baseline gap-[var(--space-inline)]">
        <dt class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
          {{ figure.label }}
        </dt>
        <dd class="text-[color:var(--color-attention)]">
          <MoneyAmount :value="figure.parts" v-if="figure.parts" />
          <span v-else>–</span>
        </dd>
      </div>
    </dl>
    <p
      v-if="draft.posted === undefined"
      class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
    >
      {{ t('loans.extension.estimateNote') }}
    </p>
  </div>
</template>
